import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

/**
 * Espera as imagens do container carregarem antes de rasterizar.
 *
 * Sem isto o html2canvas fotografa o instante em que os logos ainda não
 * chegaram, e o PDF sai com o cabeçalho em branco — de forma intermitente,
 * porque depende do cache do navegador.
 *
 * Movido de `consortium-groups/proposal-utils.ts` para cá: a jornada de
 * cotação de seguros (`seguros/cotacao/cotacaoPdf.ts`) precisa da mesma
 * paginação HTML→PDF, e o consórcio não é o único módulo que gera PDF.
 */
export const waitForImages = async (root: HTMLElement) => {
  const images = Array.from(root.querySelectorAll("img"));
  if (images.length === 0) return;
  await Promise.all(
    images.map((img) => {
      if (img.complete) return Promise.resolve();
      return new Promise<void>((resolve) => {
        const done = () => resolve();
        img.addEventListener("load", done, { once: true });
        img.addEventListener("error", done, { once: true });
      });
    }),
  );
};

/**
 * HTML → PDF: rasteriza o container e pagina em A4.
 *
 * Compartilhado entre todos os geradores de PDF do app — a lógica de fatiar
 * página é o tipo de coisa que só se corrige uma vez se existir em um lugar só.
 *
 * Remove o container no finally: ele é anexado ao body fora da tela pra o
 * html2canvas medir, e ficar pra trás vaza DOM a cada PDF gerado.
 */
export const renderContainerToPdf = async (container: HTMLDivElement, filename: string) => {
  try {
    const canvas = await html2canvas(container, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      windowWidth: 794,
      windowHeight: container.scrollHeight,
    });

    const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    const imgWidthPx = canvas.width;
    const imgHeightPx = canvas.height;
    const ratio = pageWidth / imgWidthPx;
    const totalHeightMm = imgHeightPx * ratio;

    // If content fits within 1.2x page height, scale to one page instead of splitting
    if (totalHeightMm <= pageHeight * 1.2) {
      const imgData = canvas.toDataURL("image/png");
      const renderedHeight = Math.min(totalHeightMm, pageHeight);
      const scaledWidth = (renderedHeight / totalHeightMm) * pageWidth;
      const xOff = (pageWidth - scaledWidth) / 2;
      pdf.addImage(imgData, "PNG", xOff, 0, scaledWidth, renderedHeight);
    } else {
      // Cada página gera seu próprio recorte (sliceData) abaixo — não há
      // toDataURL da página inteira aqui, que seria caro e descartado.
      let yOffset = 0;
      let page = 0;
      const minSliceMm = 20; // don't create a page with less than 20mm of content

      while (yOffset < totalHeightMm) {
        const remaining = totalHeightMm - yOffset;
        if (remaining < minSliceMm) break;
        if (page > 0) pdf.addPage();

        const sliceHeightMm = Math.min(pageHeight, remaining);
        const sliceHeightPx = sliceHeightMm / ratio;
        const sliceYPx = yOffset / ratio;

        const sliceCanvas = document.createElement("canvas");
        sliceCanvas.width = imgWidthPx;
        sliceCanvas.height = Math.ceil(sliceHeightPx);
        const ctx = sliceCanvas.getContext("2d")!;
        ctx.drawImage(canvas, 0, sliceYPx, imgWidthPx, sliceHeightPx, 0, 0, imgWidthPx, Math.ceil(sliceHeightPx));

        const sliceData = sliceCanvas.toDataURL("image/png");
        pdf.addImage(sliceData, "PNG", 0, 0, pageWidth, sliceHeightMm);

        yOffset += pageHeight;
        page++;
      }
    }

    pdf.save(filename);
  } finally {
    document.body.removeChild(container);
  }
};

/**
 * Páginas já desenhadas no tamanho A4 (794 × 1123 px) → um PDF, uma imagem
 * por página. Diferente de renderContainerToPdf, nada é fatiado: cada página
 * é fotografada inteira, então gráfico e cartão nunca saem cortados ao meio.
 *
 * JPEG em vez de PNG: página com fundo em degradê vira um PNG de vários MB.
 */
export const renderPaginasToPdf = async (paginas: HTMLElement[], filename: string) => {
  const host = document.createElement("div");
  Object.assign(host.style, { position: "fixed", left: "-10000px", top: "0", pointerEvents: "none" });
  paginas.forEach((p) => host.appendChild(p));
  document.body.appendChild(host);
  try {
    await waitForImages(host);
    const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
    const largura = pdf.internal.pageSize.getWidth();
    const altura = pdf.internal.pageSize.getHeight();
    for (let i = 0; i < paginas.length; i++) {
      const canvas = await html2canvas(paginas[i], {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        windowWidth: 794,
        windowHeight: 1123,
      });
      if (i > 0) pdf.addPage();
      pdf.addImage(canvas.toDataURL("image/jpeg", 0.92), "JPEG", 0, 0, largura, altura);
    }
    pdf.save(filename);
  } finally {
    document.body.removeChild(host);
  }
};
